import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Switch, Typography, useTheme, Collapse } from '@mui/material';

import dayjs from 'dayjs';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import ProductCard from '../ui/cards/productCard';
import GreatDatePicker from '../ui/forms/fields/datePicker';
import Loader from '../ui/loader';

import ClientAccountHeader from './clientAccountHeader';

import { getBusinessDays } from '../../redux/businessDay/businessDay.slice';
import { getAccountHistoricBalances } from '../../redux/clients/clients.slice';

const getEffectiveMonthEnd = (selectedDate, latestBusinessDate) => {
  const monthEnd = dayjs(selectedDate).endOf('month');
  const businessDate = dayjs(latestBusinessDate);

  return monthEnd.isAfter(businessDate) ? businessDate : monthEnd;
};

function ClientAccounts({
  clientAccounts = [],
  loading = false,
  clientId,
  onFetchAccounts,
  onNavigate
}) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const location = useLocation();

  const clientsList = useSelector((state) => state.clients.clients);
  const { user } = useSelector((state) => state.auth);
  const { businessDays, loadingBusinessDays } = useSelector((state) => state.businessDay);

  const { accountHistoricBalances, loadingAccountHistoricBalances } = useSelector(
    (state) => state.clients
  );

  const now = dayjs();
  const lastMonth = now.subtract(1, 'month');

  const [isMonthEnd, setIsMonthEnd] = useState(false);

  const latestBalanceDate = useMemo(() => {
    return businessDays?.date ? dayjs(businessDays.date) : dayjs().subtract(2, 'day');
  }, [businessDays?.date]);

  const { control, setValue, watch } = useForm({
    defaultValues: {
      selectedMonth: latestBalanceDate
    }
  });

  const selectedMonth = watch('selectedMonth');

  useEffect(() => {
    if (businessDays?.date || loadingBusinessDays) return;

    dispatch(getBusinessDays({ countryCode: 'ZA', lag: -2 }));
  }, [dispatch, businessDays?.date, loadingBusinessDays]);

  // NAV TOTAL
  const navTotal = useMemo(() => {
    return (clientAccounts || []).reduce((sum, item) => sum + (item.nav || 0), 0);
  }, [clientAccounts]);

  // CLIENT NAME
  const clientName = useMemo(() => {
    // My Accounts page
    if (location.pathname.startsWith('/balances')) {
      const firstName = user?.given_name || '';
      const lastName = user?.family_name || '';

      const fullName = `${firstName} ${lastName}`.trim();

      return fullName || 'Client';
    }

    // Advisor/Admin/Practice client lookup
    const list = Array.isArray(clientsList) ? clientsList : [];

    const found = list.find((c) => c.id === clientId);

    return found?.name || found?.clientName || 'Client';
  }, [location.pathname, user, clientsList, clientId]);

  const isLatest = !isMonthEnd;

  // Fetch historic balances using the same date
  // that is being used for the account balance.
  const fetchHistoricBalances = useCallback(
    (date) => {
      if (!date || !clientAccounts?.length) return;

      const selectedDate = dayjs(date);

      const fromDate = selectedDate.startOf('year').format('DD-MM-YYYY');
      const toDate = selectedDate.format('DD-MM-YYYY');

      const accountIds = clientAccounts.map((account) => account?.id).filter(Boolean);

      if (!accountIds.length) return;

      dispatch(
        getAccountHistoricBalances({
          accountIds,
          fromDate,
          toDate
        })
      );
    },
    [clientAccounts, dispatch]
  );
  // Initial load
  useEffect(() => {
    const initialDate = businessDays?.date ? dayjs(businessDays.date) : dayjs().subtract(2, 'day');

    setValue('selectedMonth', initialDate);

    onFetchAccounts?.({
      id: clientId,
      asof: initialDate.format('YYYY-MM-DD')
    });
  }, [clientId, businessDays?.date, setValue, onFetchAccounts]);

  // Fetch historic balances whenever the accounts
  // and the selected balance date are available.
  useEffect(() => {
    if (!clientAccounts?.length || !selectedMonth) return;

    fetchHistoricBalances(selectedMonth);
  }, [clientAccounts, selectedMonth, fetchHistoricBalances]);

  // Toggle logic
  const handleToggle = (event) => {
    const checked = event.target.checked;
    setIsMonthEnd(checked);

    const selected = checked
      ? getEffectiveMonthEnd(lastMonth, latestBalanceDate)
      : latestBalanceDate;

    setValue('selectedMonth', selected);

    onFetchAccounts?.({
      id: clientId,
      asof: selected.format('YYYY-MM-DD')
    });
  };

  if (loading || loadingBusinessDays) {
    return <Loader loading={true} message="Loading client accounts..." />;
  }

  return (
    <>
      {/* CONTROLS */}
      <Box
        p={2}
        width="100%"
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        gap={2}
        mb={2}>
        <Typography sx={{ color: theme.palette.accent.main }}>Latest Balances</Typography>

        <Switch checked={isMonthEnd} onChange={handleToggle} />

        <Typography sx={{ color: theme.palette.accent.main }}>Month End Balance</Typography>

        <Collapse in={isMonthEnd} orientation="horizontal">
          <Box sx={{ minWidth: 200 }}>
            <GreatDatePicker
              name="selectedMonth"
              label="Select Month"
              control={control}
              views={['year', 'month']}
              disableFuture
              minDate={dayjs('2026-01-01')}
              maxDate={lastMonth.endOf('month')}
              onAccept={(newValue) => {
                if (!newValue) return;

                const selected = getEffectiveMonthEnd(newValue, latestBalanceDate);

                setValue('selectedMonth', selected);

                onFetchAccounts?.({
                  id: clientId,
                  asof: selected.format('YYYY-MM-DD')
                });
              }}
            />
          </Box>
        </Collapse>
      </Box>

      {/* HEADER */}
      <Box p={2} width="100%">
        <ClientAccountHeader
          clientName={clientName}
          navTotal={navTotal}
          ccy="ZAR"
          isLatest={isLatest}
          paperSx={{
            backgroundColor: '#121212'
          }}
        />
      </Box>

      {/* CARDS */}
      <Box p={2} width="100%" display="flex" flexDirection="column" gap={2}>
        {clientAccounts.length ? (
          clientAccounts.map((account) => {
            const accountHistoricData =
              accountHistoricBalances?.find((item) => item.accountId === account.id)?.data || [];

            return (
              <ProductCard
                key={account.id}
                account={account}
                historicData={accountHistoricData}
                loadingHistoricData={loadingAccountHistoricBalances}
                onClick={() => onNavigate?.(account.id)}
              />
            );
          })
        ) : (
          <Box display="flex" justifyContent="center" mt={4}>
            <Typography>No client accounts found</Typography>
          </Box>
        )}

        {/* HISTORICAL AUM */}
      </Box>
    </>
  );
}

export default ClientAccounts;
